import React, { useState } from 'react';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { DialogSection } from '~/components/dialogs/dialog-section.component';
import { StyledModal as Dialog } from '~/components/dialogs/dialog.style';

export interface ImageReviewDialogProps {
  base64Image: string;
  fileType: string;
  onApprove: (base64Image: string) => void;
  onCancel: () => void;
}

export const ImageReviewDialog = ({
  base64Image,
  fileType,
  onApprove,
  onCancel,
}: ImageReviewDialogProps) => {
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    x: 0,
    y: 0,
    width: 100,
    height: 100,
  });
  const [htmlImage, setHtmlImage] = useState<HTMLImageElement | null>(null);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);

  function onImageLoad(e: React.SyntheticEvent<HTMLImageElement>) {
    const eventHtmlImage = e.currentTarget;
    setHtmlImage(eventHtmlImage);
    setCompletedCrop({
      unit: 'px',
      x: 0,
      y: 0,
      width: eventHtmlImage.width,
      height: eventHtmlImage.height,
    });
  }

  const onOk = () => {
    const scaleX = htmlImage!.naturalWidth / htmlImage!.width;
    const scaleY = htmlImage!.naturalHeight / htmlImage!.height;

    const fullScaleCropWidth = completedCrop!.width * scaleX;
    const fullScaleCropHeight = completedCrop!.height * scaleY;
    const fullScaleCropX = completedCrop!.x * scaleX;
    const fullScaleCropY = completedCrop!.y * scaleY;

    const canvas = document.createElement('canvas');
    canvas.width = fullScaleCropWidth;
    canvas.height = fullScaleCropHeight;
    const canvasContext = canvas.getContext('2d');
    canvasContext!.drawImage(
      htmlImage!,
      fullScaleCropX,
      fullScaleCropY,
      fullScaleCropWidth,
      fullScaleCropHeight,
      0,
      0,
      fullScaleCropWidth,
      fullScaleCropHeight,
    );
    const base64Image = canvas.toDataURL(fileType);
    onApprove(base64Image);
  };

  return (
    <Dialog isOpen onDismiss={onCancel} aria-label="Review image">
      <Dialog.Header title="Review and crop image" onClose={onCancel} />
      <DialogSection>
        <ReactCrop crop={crop} onChange={setCrop} onComplete={setCompletedCrop}>
          <img src={base64Image} alt="Uploaded document" onLoad={onImageLoad} />
        </ReactCrop>
      </DialogSection>
      <Dialog.FooterStacked
        disabled={!completedCrop}
        primaryText="Ok"
        onSubmit={onOk}
        secondaryText="Cancel"
        onCancel={onCancel}
      />
    </Dialog>
  );
};
